// ─── Settings → API Keys → Data API Documentation ────────────────────────────
// Rendered underneath the API-keys table once at least one active key exists
// for the project. Content mirrors the legacy Symfony docs and the current
// live docs page at:
//   https://www.nexsens.com/knowledge-base-v2/software/wqdatalive/user-guide/wqdata-live-data-api-doc
//
// Endpoints are grouped into three collapsible sections — Devices, Parameters,
// Data — matching the sidebar layout of the canonical NexSens docs page.
// Every endpoint is a GET request. Auth is a 32-char `apiKey` query
// parameter (issued from the table above). Responses are JSON.

import { useState } from 'react'
import { ChevronDown, ChevronRight, Copy, Check } from 'lucide-react'

// ─── Endpoint dataset ────────────────────────────────────────────────────────
// `label` is the short human name shown in the group list (e.g. "Get devices").
// `path` / `method` / `queryParams` / `exampleUrl` / `exampleResponse` drive
// the detail panel that opens on click.

const DEVICE_ENDPOINTS = [
  {
    id: 'devices-list',
    method: 'GET',
    label: 'Get devices',
    path: '/api/v1/devices',
    description: 'Returns every active device in the project, grouped by site, with each sensor UID and visibility flag.',
    queryParams: [
      { name: 'apiKey', type: 'string (32)', required: true, description: 'Secret key from the table above.' },
    ],
    exampleUrl: '/api/v1/devices?apiKey=YOUR_API_KEY',
    exampleResponse: `{
  "devices": [
    {
      "id": 1234,
      "name": "X2-CB-01",
      "site": "Pond Buoy",
      "lastContact": "2026-07-06 12:00:00",
      "sensors": [
        { "uid": "S00001", "name": "T-Node", "visible": true }
      ]
    }
  ]
}`,
  },
  {
    id: 'device-single',
    method: 'GET',
    label: 'Get single device',
    path: '/api/v1/devices/{deviceID}',
    description: 'Single device by its numeric id.',
    queryParams: [
      { name: 'apiKey', type: 'string (32)', required: true, description: 'Secret key from the table above.' },
    ],
    exampleUrl: '/api/v1/devices/1234?apiKey=YOUR_API_KEY',
    exampleResponse: `{
  "id": 1234,
  "name": "X2-CB-01",
  "site": "Pond Buoy",
  "lastContact": "2026-07-06 12:00:00",
  "sensors": [
    { "uid": "S00001", "name": "T-Node", "visible": true }
  ]
}`,
  },
]

const PARAMETER_ENDPOINTS = [
  {
    id: 'parameters-list',
    method: 'GET',
    label: 'Get parameters',
    path: '/api/v1/devices/{deviceID}/parameters',
    description: 'All parameters for the given device — one row per parameter, including unit, precision, and whether it\'s calculated.',
    queryParams: [
      { name: 'apiKey', type: 'string (32)', required: true, description: 'Secret key from the table above.' },
    ],
    exampleUrl: '/api/v1/devices/1234/parameters?apiKey=YOUR_API_KEY',
    exampleResponse: `{
  "parameters": [
    {
      "id": 78901,
      "name": "Water Temperature",
      "visible": true,
      "unit": "°C",
      "precision": 2,
      "sensor": "T-Node",
      "sensorUID": "S00001",
      "isCalculated": false
    }
  ]
}`,
  },
  {
    id: 'parameter-single',
    method: 'GET',
    label: 'Get single parameter',
    path: '/api/v1/devices/{deviceID}/parameters/{paramID}',
    description: 'Single parameter by id.',
    queryParams: [
      { name: 'apiKey', type: 'string (32)', required: true, description: 'Secret key from the table above.' },
    ],
    exampleUrl: '/api/v1/devices/1234/parameters/78901?apiKey=YOUR_API_KEY',
    exampleResponse: `{
  "id": 78901,
  "name": "Water Temperature",
  "visible": true,
  "unit": "°C",
  "precision": 2,
  "sensor": "T-Node",
  "sensorUID": "S00001",
  "isCalculated": false
}`,
  },
]

const DATA_ENDPOINTS = [
  {
    id: 'parameters-data',
    method: 'GET',
    label: 'Get data from multiple parameters',
    path: '/api/v1/devices/{deviceID}/parameters/data',
    description: 'Time-series values for several parameters on one device in a single request. Point limit is applied per-parameter based on the smallest log interval, so all series stay aligned to the same window.',
    queryParams: [
      { name: 'apiKey',        type: 'string (32)',    required: true,  description: 'Secret key from the table above.' },
      { name: 'from',          type: 'datetime',       required: true,  description: 'UTC start, `YYYY-MM-DD HH:MM:SS`.' },
      { name: 'to',            type: 'datetime',       required: true,  description: 'UTC end, `YYYY-MM-DD HH:MM:SS`.' },
      { name: 'parameterIds',  type: 'csv | "all"',    required: false, description: 'Comma-separated parameter ids. Defaults to `all` — every visible parameter on the device.' },
    ],
    exampleUrl: '/api/v1/devices/1234/parameters/data?apiKey=YOUR_API_KEY&from=2026-07-01%2000:00:00&to=2026-07-02%2000:00:00&parameterIds=78901,78902',
    exampleResponse: `{
  "parameters": [
    {
      "id":   78901,
      "name": "Water Temperature",
      "unit": "°C",
      "data": [
        ["2026-07-01 00:00:00", 18.42],
        ["2026-07-01 00:10:00", 18.47]
      ]
    },
    {
      "id":   78902,
      "name": "Dissolved Oxygen",
      "unit": "mg/L",
      "data": [
        ["2026-07-01 00:00:00", 8.11],
        ["2026-07-01 00:10:00", 8.09]
      ]
    }
  ],
  "info": {
    "total":    288,
    "count":    288,
    "more":     false,
    "timezone": "UTC"
  }
}`,
  },
  {
    id: 'parameter-data',
    method: 'GET',
    label: 'Get data from a single parameter',
    path: '/api/v1/devices/{deviceID}/parameters/{paramID}/data',
    description: 'Time-series values for one parameter within a UTC window. Capped at 1 000 data points per call; if more are available, `info.more` is true and you should paginate by advancing `from` past the last returned timestamp.',
    queryParams: [
      { name: 'apiKey', type: 'string (32)', required: true, description: 'Secret key from the table above.' },
      { name: 'from',   type: 'datetime',    required: true, description: 'UTC start, `YYYY-MM-DD HH:MM:SS`.' },
      { name: 'to',     type: 'datetime',    required: true, description: 'UTC end, `YYYY-MM-DD HH:MM:SS`. Range is capped by the server\'s days-limit.' },
    ],
    exampleUrl: '/api/v1/devices/1234/parameters/78901/data?apiKey=YOUR_API_KEY&from=2026-07-01%2000:00:00&to=2026-07-06%2000:00:00',
    exampleResponse: `{
  "data": [
    ["2026-07-01 00:00:00", 18.42],
    ["2026-07-01 00:10:00", 18.47],
    ["2026-07-01 00:20:00", 18.51]
  ],
  "info": {
    "total":    720,
    "count":    720,
    "more":     false,
    "timezone": "UTC"
  }
}`,
  },
  {
    id: 'parameters-latest',
    method: 'GET',
    label: 'Get the latest data from multiple parameters',
    path: '/api/v1/devices/{deviceID}/parameters/data/latest',
    description: 'The most recent value for every parameter on the device, or the subset named in `parameterIds`.',
    queryParams: [
      { name: 'apiKey',        type: 'string (32)', required: true,  description: 'Secret key from the table above.' },
      { name: 'parameterIds',  type: 'csv | "all"', required: false, description: 'Comma-separated parameter ids. Defaults to `all`.' },
    ],
    exampleUrl: '/api/v1/devices/1234/parameters/data/latest?apiKey=YOUR_API_KEY',
    exampleResponse: `{
  "parameters": [
    { "id": 78901, "name": "Water Temperature", "unit": "°C",   "timestamp": "2026-07-06 11:50:00", "value": 19.14 },
    { "id": 78902, "name": "Dissolved Oxygen",  "unit": "mg/L", "timestamp": "2026-07-06 11:50:00", "value":  8.09 }
  ]
}`,
  },
  {
    id: 'parameter-latest',
    method: 'GET',
    label: 'Get the latest data from a single parameter',
    path: '/api/v1/devices/{deviceID}/parameters/{paramID}/data/latest',
    description: 'The most recent value for one parameter — no time window, no pagination.',
    queryParams: [
      { name: 'apiKey', type: 'string (32)', required: true, description: 'Secret key from the table above.' },
    ],
    exampleUrl: '/api/v1/devices/1234/parameters/78901/data/latest?apiKey=YOUR_API_KEY',
    exampleResponse: `{
  "id":        78901,
  "name":      "Water Temperature",
  "unit":      "°C",
  "timestamp": "2026-07-06 11:50:00",
  "value":     19.14
}`,
  },
]

// Ordered list of the three groups. Each group renders as a collapsible
// panel that contains its endpoints; groups start expanded by default so
// the docs are visible on load.
const GROUPS = [
  { id: 'devices',    title: 'Devices',    endpoints: DEVICE_ENDPOINTS },
  { id: 'parameters', title: 'Parameters', endpoints: PARAMETER_ENDPOINTS },
  { id: 'data',       title: 'Data',       endpoints: DATA_ENDPOINTS },
]

// Status-code table shared by every endpoint. Kept in one place so the docs
// only carry the endpoint-specific stuff in each card.
const STATUS_CODES = [
  { code: 200, meaning: 'OK — request succeeded.' },
  { code: 400, meaning: 'Bad Request — malformed parameter, from > to, or date range beyond the configured days-limit.' },
  { code: 403, meaning: 'Forbidden — invalid `apiKey`, request IP not in the key\'s allow-list, or hourly quota exceeded.' },
  { code: 404, meaning: 'Not Found — device or parameter does not belong to this project.' },
  { code: 500, meaning: 'Server Error — unexpected failure. Response body carries a `message` describing the cause.' },
]

const MethodPill = ({ method }) => (
  <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold rounded ${
    method === 'GET' ? 'bg-emerald-600 text-white'
                     : 'bg-blue-600 text-white'
  }`}>
    {method}
  </span>
)

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText(text).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        })
      }}
      className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#358ECD] transition"
      title="Copy"
    >
      {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

// Detail panel that expands under a selected endpoint row. All request
// info + example request + example response live in here.
function EndpointDetails({ ep, baseUrl }) {
  // exampleUrl already starts with "/api/…" — concatenate with the host as-is.
  const fullUrl = baseUrl + ep.exampleUrl
  return (
    <div className="px-6 py-4 space-y-4 bg-white border-t border-gray-100">
      <div>
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <MethodPill method={ep.method} />
          <code className="text-xs font-mono text-gray-800 break-all">{ep.path}</code>
        </div>
        <p className="text-sm text-gray-700">{ep.description}</p>
      </div>

      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Query Parameters</h4>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200 text-gray-600">
              <th className="text-left py-1 pr-3 font-semibold">Name</th>
              <th className="text-left py-1 pr-3 font-semibold">Type</th>
              <th className="text-left py-1 pr-3 font-semibold">Required</th>
              <th className="text-left py-1 font-semibold">Description</th>
            </tr>
          </thead>
          <tbody>
            {ep.queryParams.map((qp) => (
              <tr key={qp.name} className="border-b border-gray-100">
                <td className="py-1 pr-3 font-mono text-gray-800">{qp.name}</td>
                <td className="py-1 pr-3 text-gray-700">{qp.type}</td>
                <td className="py-1 pr-3">
                  {qp.required
                    ? <span className="text-red-600">Yes</span>
                    : <span className="text-gray-500">No</span>}
                </td>
                <td className="py-1 text-gray-700">{qp.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Example Request</h4>
          <CopyButton text={fullUrl} />
        </div>
        <pre className="bg-gray-900 text-gray-100 text-xs p-3 rounded overflow-x-auto">
          <code>{`curl -X GET "${fullUrl}"`}</code>
        </pre>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Example Response (200 OK)</h4>
          <CopyButton text={ep.exampleResponse} />
        </div>
        <pre className="bg-gray-50 border border-gray-200 text-xs p-3 rounded overflow-x-auto">
          <code>{ep.exampleResponse}</code>
        </pre>
      </div>
    </div>
  )
}

// A single group panel (Devices / Parameters / Data). Header toggles the
// endpoint list open/closed; clicking an endpoint row toggles its details.
function GroupPanel({ group, baseUrl, initialOpen = true }) {
  const [open, setOpen] = useState(initialOpen)
  const [activeId, setActiveId] = useState(null)

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition text-left"
      >
        <span className="text-base font-semibold text-[#7C3AED]">{group.title}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>

      {open && (
        <div className="bg-gray-50 border-t border-gray-200">
          {group.endpoints.map((ep) => {
            const active = activeId === ep.id
            return (
              <div key={ep.id} className="border-b border-gray-200 last:border-b-0">
                <button
                  type="button"
                  onClick={() => setActiveId(active ? null : ep.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition ${
                    active ? 'bg-gray-200' : 'bg-white hover:bg-gray-50'
                  }`}
                >
                  <MethodPill method={ep.method} />
                  <span className="text-sm text-gray-800">{ep.label}</span>
                  <span className="ml-auto">
                    {active
                      ? <ChevronDown  className="w-4 h-4 text-gray-500" />
                      : <ChevronRight className="w-4 h-4 text-gray-500" />}
                  </span>
                </button>
                {active && <EndpointDetails ep={ep} baseUrl={baseUrl} />}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default function ApiDocsSection() {
  // Base URL is resolved at runtime — the same origin the SPA is served from,
  // so the docs point at the customer's own deployment (stage / prod / self-host).
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : ''

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 w-full max-w-5xl">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-800">Data API Documentation</h2>
        <p className="text-xs text-gray-600 mt-1">
          Read-only endpoints for pulling device metadata and sensor readings. Authenticate every request
          with an <code className="px-1 bg-gray-100 rounded">apiKey</code> query parameter from the table above.
          The hourly quota (currently 240 calls/hour) is enforced across all keys on this project.
        </p>
      </div>

      {/* Base URL */}
      <div className="mb-4 flex items-center gap-2 text-xs">
        <span className="font-semibold text-gray-600">Base URL:</span>
        <code className="px-2 py-0.5 bg-gray-100 rounded font-mono text-gray-800">{baseUrl}</code>
      </div>

      {/* Grouped endpoints — Devices / Parameters / Data */}
      <div className="space-y-3">
        {GROUPS.map((g) => (
          <GroupPanel key={g.id} group={g} baseUrl={baseUrl} />
        ))}
      </div>

      {/* Status codes */}
      <div className="mt-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Response Status Codes</h3>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200 text-gray-600">
              <th className="text-left py-1 pr-3 font-semibold w-20">Code</th>
              <th className="text-left py-1 font-semibold">Meaning</th>
            </tr>
          </thead>
          <tbody>
            {STATUS_CODES.map((s) => (
              <tr key={s.code} className="border-b border-gray-100">
                <td className="py-1 pr-3 font-mono text-gray-800">{s.code}</td>
                <td className="py-1 text-gray-700">{s.meaning}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Reference to canonical doc */}
      <div className="mt-4 text-xs text-gray-500">
        Canonical reference:{' '}
        <a
          href="https://www.nexsens.com/knowledge-base-v2/software/wqdatalive/user-guide/wqdata-live-data-api-doc"
          target="_blank" rel="noopener noreferrer"
          className="text-[#358ECD] hover:underline"
        >
          NexSens WQData LIVE Data API Doc
        </a>
      </div>
    </div>
  )
}
